import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getFundCategories, createFundCategory, updateFundCategory, deleteFundCategory,
  getExpenseCategories, createExpenseCategory, updateExpenseCategory, deleteExpenseCategory,
} from '../../services/api';
import { FiPlus, FiEdit2, FiX, FiBell, FiTrash2 } from 'react-icons/fi';
import Toast from '../../components/Toast';

const freqLabel = { once: 'One-time', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' };
const freqColor = { once: 'pill-yellow', weekly: 'pill-blue', monthly: 'pill-purple', yearly: 'pill-green' };

// Form for Contribution types (with recurring frequency fields)
const ContribForm = ({ data, onChange }) => (
  <div className="form-grid">
    <div className="form-group form-full">
      <label>Name <span className="required">*</span></label>
      <input value={data.name} onChange={e => onChange({ ...data, name: e.target.value })} placeholder="e.g. Tithe" />
    </div>
    <div className="form-group form-full">
      <label>Description</label>
      <input value={data.description || ''} onChange={e => onChange({ ...data, description: e.target.value })} />
    </div>
    <div className="form-group form-full">
      <label>Recurring Frequency</label>
      <p style={{ fontSize: 11, color: '#9ca3af', margin: '2px 0 6px' }}>Optional — set this to allow members to subscribe to this type</p>
      <select value={data.frequency || ''} onChange={e => onChange({ ...data, frequency: e.target.value })}>
        <option value="">None</option>
        <option value="once">One-time</option>
        <option value="weekly">Weekly</option>
        <option value="monthly">Monthly</option>
        <option value="yearly">Yearly</option>
      </select>
    </div>
    {data.frequency && data.frequency !== 'once' && (
      <div className="form-group form-full">
        <label>Due Day</label>
        <p style={{ fontSize: 11, color: '#9ca3af', margin: '2px 0 6px' }}>
          {data.frequency === 'monthly' ? 'Day of the month (1–28)' : data.frequency === 'weekly' ? '1 = Monday … 7 = Sunday' : 'Day of January (1–31)'}
        </p>
        <input type="number" value={data.due_day || 1} min={1} max={data.frequency === 'monthly' ? 28 : data.frequency === 'weekly' ? 7 : 31}
          onChange={e => onChange({ ...data, due_day: parseInt(e.target.value) || 1 })}
          style={{ maxWidth: 120 }} />
      </div>
    )}
    {/* Anonymous collection — for ሞባእ, tray offerings, etc.
        When ticked, Cash Receipts of this type won't require a payer name. */}
    <div className="form-group form-full" style={{ marginTop: 4 }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!data.is_anonymous}
          onChange={e => onChange({ ...data, is_anonymous: e.target.checked })}
          style={{ marginTop: 3 }}
        />
        <span>
          <span style={{ fontWeight: 600, color: '#1f2937' }}>Anonymous collection</span>
          <span style={{ display: 'block', fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
            Cash Receipts of this type won't require a payer name — for ሞባእ, tray offerings, and other congregation-wide gatherings.
          </span>
        </span>
      </label>
    </div>
    {/* v1.9.99 — Flexible pledge (Tithe-style). When ticked, any payment > 0
        marks that month's period as PAID regardless of pledge amount. */}
    <div className="form-group form-full" style={{ marginTop: 4 }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!data.is_flexible}
          onChange={e => onChange({ ...data, is_flexible: e.target.checked })}
          style={{ marginTop: 3 }}
        />
        <span>
          <span style={{ fontWeight: 600, color: '#1f2937' }}>Flexible pledge (Tithe-style)</span>
          <span style={{ display: 'block', fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
            Pledge is a reference only. Any payment &gt; 0 marks that period paid, so a member tithing K800 against a K1,020 reference pledge isn't shown as still owing K220. Leave unticked for Monthly Contribution and other fixed-amount pledges.
          </span>
        </span>
      </label>
    </div>
  </div>
);

// Simpler form for Expense types
const ExpenseForm = ({ data, onChange }) => (
  <div className="form-grid">
    <div className="form-group form-full">
      <label>Name <span className="required">*</span></label>
      <input value={data.name} onChange={e => onChange({ ...data, name: e.target.value })} placeholder="e.g. Utilities" />
    </div>
    <div className="form-group form-full">
      <label>Description</label>
      <input value={data.description || ''} onChange={e => onChange({ ...data, description: e.target.value })} />
    </div>
  </div>
);

const blankContrib = { name: '', description: '', frequency: '', due_day: 1, is_anonymous: 0, is_flexible: 0 };
const blankExpense = { name: '', description: '' };

const FundCategories = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('contribution'); // 'contribution' | 'expense'
  const [funds, setFunds] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankContrib);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const fetchFunds = () => getFundCategories({ all: true }).then(r => setFunds(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  const fetchExpenses = () => getExpenseCategories().then(r => setExpenses(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { fetchFunds(); fetchExpenses(); }, []);

  const isContrib = tab === 'contribution';
  const items = isContrib ? funds : expenses;

  const openAdd = () => {
    setForm(isContrib ? { ...blankContrib } : { ...blankExpense });
    setError('');
    setShowAdd(true);
  };

  const openEdit = (row) => {
    setEditing({ ...row });
    setError('');
  };

  const handleAdd = async () => {
    setError('');
    if (!form.name?.trim()) return setError('Name is required');
    setSaving(true);
    try {
      if (isContrib) {
        await createFundCategory(form);
        showToast('Contribution type created');
        fetchFunds();
      } else {
        await createExpenseCategory(form);
        showToast('Expense type created');
        fetchExpenses();
      }
      setShowAdd(false);
      setForm(isContrib ? { ...blankContrib } : { ...blankExpense });
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setError('');
    if (!editing.name?.trim()) return setError('Name is required');
    setSaving(true);
    try {
      if (isContrib) {
        await updateFundCategory(editing.id, editing);
        showToast('Updated');
        fetchFunds();
      } else {
        await updateExpenseCategory(editing.id, editing);
        showToast('Updated');
        fetchExpenses();
      }
      setEditing(null);
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (row) => {
    try {
      if (isContrib) {
        await updateFundCategory(row.id, { ...row, active: row.active ? 0 : 1 });
        fetchFunds();
      } else {
        await updateExpenseCategory(row.id, { ...row, active: row.active ? 0 : 1 });
        fetchExpenses();
      }
      showToast(row.active ? 'Deactivated' : 'Activated');
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to update', 'error');
    }
  };

  const handleDeleteFund = async (row) => {
    if (!window.confirm(`Delete contribution type "${row.name}"? This cannot be undone.`)) return;
    try {
      await deleteFundCategory(row.id);
      showToast('Deleted', 'error');
      fetchFunds();
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to delete', 'error');
    }
  };

  const handleDeleteExpense = async (row) => {
    if (!window.confirm(`Delete expense type "${row.name}"?`)) return;
    try {
      await deleteExpenseCategory(row.id);
      showToast('Deleted', 'error');
      fetchExpenses();
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to delete', 'error');
    }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>{t('nav.contributionTypes')}</h1>
          <p>{t('fundCategories.subtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><FiPlus /> {t('common.add')}</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: 16 }}>
        {[
          { key: 'contribution', label: t('fundCategories.contributionTab'), count: funds.length },
          { key: 'expense', label: t('fundCategories.expenseTab'), count: expenses.length },
        ].map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)} style={{
            padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 13,
            color: tab === tb.key ? '#4f46e5' : '#6b7280',
            borderBottom: tab === tb.key ? '2px solid #4f46e5' : '2px solid transparent', marginBottom: -2,
          }}>
            {tb.label}
            <span style={{ marginLeft: 6, background: tab === tb.key ? '#4f46e5' : '#e5e7eb', color: tab === tb.key ? '#fff' : '#6b7280', borderRadius: 10, padding: '1px 8px', fontSize: 11 }}>{tb.count}</span>
          </button>
        ))}
      </div>

      <div className="table-card">
        <table>
          <thead>
            {isContrib ? (
              <tr><th>#</th><th>{t('common.name')}</th><th>{t('table.description')}</th><th>{t('fundCategories.frequency')}</th><th>{t('subscriptions.dueDate')}</th><th>{t('common.status')}</th><th>{t('common.actions')}</th></tr>
            ) : (
              <tr><th>#</th><th>{t('common.name')}</th><th>{t('table.description')}</th><th>{t('common.status')}</th><th>{t('common.actions')}</th></tr>
            )}
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={isContrib ? 7 : 5}><div className="empty-state"><div className="empty-icon"><FiBell /></div><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : items.map((row, i) => (
              <tr key={row.id}>
                <td data-label="#">{i + 1}</td>
                <td data-label={t('common.name')}><strong>{row.name}</strong></td>
                <td data-label={t('table.description')}>{row.description || '—'}</td>
                {isContrib && (
                  <>
                    <td data-label={t('fundCategories.frequency')}>{row.frequency ? <span className={`pill ${freqColor[row.frequency]}`}>{freqLabel[row.frequency]}</span> : <span style={{ color: '#9ca3af', fontSize: 12 }}>{t('common.none')}</span>}</td>
                    <td data-label={t('subscriptions.dueDate')}>{row.frequency && row.frequency !== 'once' ? `${t('subscriptions.dueDate')} ${row.due_day || 1}` : '—'}</td>
                  </>
                )}
                <td data-label={t('common.status')}><span className={`pill ${row.active ? 'pill-green' : 'pill-gray'}`}>{row.active ? t('common.active') : t('common.inactive')}</span></td>
                <td data-label={t('common.actions')}>
                  <div className="action-btns">
                    <button className="btn-icon" onClick={() => openEdit(row)}><FiEdit2 /></button>
                    <button className="btn btn-sm btn-secondary" onClick={() => handleToggle(row)}>{row.active ? t('common.inactive') : t('common.active')}</button>
                    {isContrib
                      ? <button className="btn-icon danger" onClick={() => handleDeleteFund(row)}><FiTrash2 /></button>
                      : <button className="btn-icon danger" onClick={() => handleDeleteExpense(row)}><FiTrash2 /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <div><h3>{isContrib ? t('fundCategories.addContribution') : t('fundCategories.addExpense')}</h3></div>
              <button className="btn-icon" onClick={() => setShowAdd(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              {isContrib ? <ContribForm data={form} onChange={setForm} /> : <ExpenseForm data={form} onChange={setForm} />}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <div><h3>{isContrib ? t('fundCategories.editContribution') : t('fundCategories.editExpense')}</h3><p>{editing.name}</p></div>
              <button className="btn-icon" onClick={() => setEditing(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              {isContrib ? <ContribForm data={editing} onChange={setEditing} /> : <ExpenseForm data={editing} onChange={setEditing} />}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default FundCategories;
